import React from 'react'
import { useCoachChat } from './CoachChatContext'
import { useTranslation } from '~/hooks/useTranslation'
import WaitingRoomPersonIcon from './WaitingRoomPersonIcon'
import {
  getListRowClass,
  getWaitingRoomCardClass,
  getWaitingRoomCardHeadingClass,
  getCoachBadgeClass,
  getPersonNameClass,
} from './coachDashboardStyles'

const getCoachBadge = (status, t, getDarkModeClasses) => {
  if (status === 'busy') {
    return {
      label: t('coach_dashboard.coach_in_session', 'In Session'),
      className: getCoachBadgeClass('busy', getDarkModeClasses),
    }
  }

  if (status === 'away') {
    return {
      label: t('coach_dashboard.coach_away', 'Away'),
      className: getCoachBadgeClass('away', getDarkModeClasses),
    }
  }

  return {
    label: t('coach_dashboard.coach_available', 'Available'),
    className: getCoachBadgeClass('available', getDarkModeClasses),
  }
}

const CoachesList = ({ coaches, embedded = false }) => {
  const { getDarkModeClasses } = useCoachChat()
  const { t } = useTranslation('coach_chat')

  const containerClasses = embedded
    ? `${getWaitingRoomCardClass(getDarkModeClasses)} h-full`
    : `flex h-full min-h-0 flex-col overflow-hidden rounded-lg border p-4 ${getDarkModeClasses(
        'border-gray-200 bg-white',
        'border-gray-600 bg-gray-800',
      )}`

  const headingClass = embedded
    ? getWaitingRoomCardHeadingClass(getDarkModeClasses)
    : `mb-3 flex-shrink-0 text-sm font-medium ${getDarkModeClasses(
        'text-gray-900',
        'text-white',
      )}`

  return (
    <div className={containerClasses}>
      <h3 className={headingClass}>
        {t('coach_dashboard.coaches', 'Coaches')}
      </h3>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
        {coaches && coaches.length > 0 ? (
          coaches.map((coach) => {
            const badge = getCoachBadge(
              coach.status || 'online',
              t,
              getDarkModeClasses,
            )

            if (embedded) {
              return (
                <div
                  key={coach.easyrtcid}
                  className={getListRowClass(getDarkModeClasses)}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <WaitingRoomPersonIcon />
                    <span className={getPersonNameClass(getDarkModeClasses)}>
                      {coach.displayName || coach.username}
                    </span>
                  </div>
                  <span className={`ml-2 flex-shrink-0 ${badge.className}`}>
                    {badge.label}
                  </span>
                </div>
              )
            }

            return (
              <div
                key={coach.easyrtcid}
                className={`flex items-center justify-between border-b py-2 last:border-b-0 ${getDarkModeClasses(
                  'border-gray-100',
                  'border-gray-700',
                )}`}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <WaitingRoomPersonIcon />
                  <span
                    className={`truncate text-sm ${getDarkModeClasses(
                      'text-gray-700',
                      'text-gray-300',
                    )}`}
                  >
                    {coach.displayName || coach.username}
                  </span>
                </div>
                <span className={badge.className}>{badge.label}</span>
              </div>
            )
          })
        ) : (
          <p
            className={`py-4 text-center font-['Roboto'] text-sm ${getDarkModeClasses(
              'text-gray-500',
              'text-gray-400',
            )}`}
          >
            {t('coach_dashboard.no_coaches', 'No other coaches in room')}
          </p>
        )}
      </div>
    </div>
  )
}

export default CoachesList
